import React, { useState } from 'react';
import resourceService, { Resource } from '../../services/resourceService';

interface Segment {
  id: number;
  name: string;
  description: string;
  active_leads: number;
}

interface ResourceListProps {
  resources: Resource[];
  segments: Segment[];
  onResourceUpdated: () => void;
  onResourceDeleted: (resourceId: number) => void;
}

const ResourceList: React.FC<ResourceListProps> = ({
  resources,
  segments,
  onResourceUpdated,
  onResourceDeleted
}) => {
  const [expandedResource, setExpandedResource] = useState<number | null>(null);
  const [editingResource, setEditingResource] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Resource>>({});
  const [loading, setLoading] = useState<number | null>(null);

  const getCategoryColor = (category: string): string => {
    const colors: { [key: string]: string } = {
      'autism': 'bg-blue-100 text-blue-800',
      'adhd': 'bg-green-100 text-green-800',
      'learning-differences': 'bg-purple-100 text-purple-800',
      'speech-language': 'bg-orange-100 text-orange-800',
      'behavioral-support': 'bg-red-100 text-red-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['general'];
  };

  const getFileIcon = (fileType: string): string => {
    switch (fileType.toLowerCase()) {
      case 'pdf': return '📄';
      case 'docx':
      case 'doc': return '📝';
      case 'mp4':
      case 'webm': return '🎥';
      case 'jpg':
      case 'jpeg':
      case 'png': return '🖼️';
      default: return '📎';
    }
  };

  const handleToggleExpand = (resourceId: number) => {
    setExpandedResource(expandedResource === resourceId ? null : resourceId);
  };

  const handleStartEdit = (resource: Resource) => {
    setEditingResource(resource.id);
    setEditForm({
      title: resource.title,
      description: resource.description,
      category: resource.category,
      priority: resource.priority,
      is_active: resource.is_active,
      require_email: resource.require_email
    });
  };

  const handleCancelEdit = () => {
    setEditingResource(null);
    setEditForm({});
  };

  const handleSaveEdit = async (resourceId: number) => {
    try {
      setLoading(resourceId);
      await resourceService.updateResource(resourceId, editForm);
      setEditingResource(null);
      setEditForm({});
      onResourceUpdated();
    } catch (error) {
      console.error('Error updating resource:', error);
      alert('Failed to update resource');
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async (resourceId: number, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      try {
        setLoading(resourceId);
        await onResourceDeleted(resourceId);
      } catch (error) {
        console.error('Error deleting resource:', error);
        alert('Failed to delete resource');
      } finally {
        setLoading(null);
      }
    }
  };

  const handleToggleActive = async (resourceId: number, currentStatus: boolean) => {
    try {
      setLoading(resourceId);
      await resourceService.updateResource(resourceId, { is_active: !currentStatus });
      onResourceUpdated();
    } catch (error) {
      console.error('Error toggling resource status:', error);
      alert('Failed to update resource status');
    } finally {
      setLoading(null);
    }
  };

  const generateTestDownloadLink = async (resourceId: number) => {
    try {
      const token = await resourceService.generateDownloadToken(
        resourceId,
        'test@example.com'
      );
      const fullUrl = `${window.location.origin}${token.download_url}`;
      
      // Copy to clipboard
      navigator.clipboard.writeText(fullUrl);
      alert(`Download link copied to clipboard!\n\nValid until: ${new Date(token.expires_at).toLocaleString()}`);
    } catch (error) {
      console.error('Error generating test link:', error);
      alert('Failed to generate test download link');
    }
  };

  if (resources.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {resources.map((resource) => (
        <div
          key={resource.id}
          className={`bg-white border rounded-lg overflow-hidden transition-all duration-200 ${
            expandedResource === resource.id ? 'shadow-lg' : 'shadow-sm hover:shadow-md'
          }`}
        >
          {/* Resource Header */}
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {editingResource === resource.id ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editForm.title || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full text-lg font-semibold border border-gray-300 rounded px-2 py-1"
                    />
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full text-sm text-gray-600 border border-gray-300 rounded px-2 py-1"
                    />
                    <div className="flex gap-3">
                      <select
                        value={editForm.category || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="autism">Autism Support</option>
                        <option value="adhd">ADHD Management</option>
                        <option value="learning-differences">Learning Differences</option>
                        <option value="speech-language">Speech & Language</option>
                        <option value="behavioral-support">Behavioral Support</option>
                        <option value="general">General Resources</option>
                      </select>
                      <input
                        type="number"
                        value={editForm.priority || 0}
                        onChange={(e) => setEditForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                        min="1"
                        max="10"
                        className="w-20 text-sm border border-gray-300 rounded px-2 py-1"
                        placeholder="Priority"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{resource.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(resource.category)}`}>
                        {resource.category}
                      </span>
                      {!resource.is_active && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
                  </>
                )}

                {/* Resource Metadata */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    📁 {resource.file_count} files
                  </span>
                  <span className="flex items-center gap-1">
                    📊 Priority {resource.priority}
                  </span>
                  <span className="flex items-center gap-1">
                    📥 {resource.download_count} downloads
                  </span>
                  {resource.require_email && (
                    <span className="flex items-center gap-1">
                      ✉️ Email required
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    🏷️ {resource.assigned_segments?.length || 0} segments
                  </span>
                  {(resource.assigned_quiz_profiles?.length || 0) > 0 && (
                    <span className="flex items-center gap-1 text-purple-600">
                      📝 {resource.assigned_quiz_profiles?.length || 0} quiz profiles
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 ml-4">
                {editingResource === resource.id ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(resource.id)}
                      disabled={loading === resource.id}
                      className="text-green-600 hover:text-green-700 text-sm px-2 py-1 rounded"
                    >
                      {loading === resource.id ? '⏳' : '✅'} Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="text-gray-600 hover:text-gray-700 text-sm px-2 py-1 rounded"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleStartEdit(resource)}
                      className="text-gray-600 hover:text-gray-700 text-sm px-2 py-1 rounded"
                      title="Edit resource"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleToggleActive(resource.id, resource.is_active)}
                      disabled={loading === resource.id}
                      className={`text-sm px-2 py-1 rounded ${
                        resource.is_active 
                          ? 'text-orange-600 hover:text-orange-700' 
                          : 'text-green-600 hover:text-green-700'
                      }`}
                      title={resource.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {loading === resource.id ? '⏳' : (resource.is_active ? '⏸️' : '▶️')}
                    </button>
                    <button
                      onClick={() => generateTestDownloadLink(resource.id)}
                      className="text-blue-600 hover:text-blue-700 text-sm px-2 py-1 rounded"
                      title="Generate test download link"
                    >
                      🔗
                    </button>
                    <button
                      onClick={() => handleDelete(resource.id, resource.title)}
                      disabled={loading === resource.id}
                      className="text-red-600 hover:text-red-700 text-sm px-2 py-1 rounded"
                      title="Delete resource"
                    >
                      {loading === resource.id ? '⏳' : '🗑️'}
                    </button>
                  </>
                )}

                <button
                  onClick={() => handleToggleExpand(resource.id)}
                  className="text-gray-400 hover:text-gray-600 text-sm px-2 py-1 rounded"
                >
                  {expandedResource === resource.id ? '🔼' : '🔽'}
                </button>
              </div>
            </div>
          </div>

          {/* Expanded Details */}
          {expandedResource === resource.id && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Files */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Files ({resource.file_count})</h4>
                  <div className="space-y-2">
                    {resource.files?.map((file, index) => (
                      <div key={file.id} className="flex items-center gap-2 text-sm">
                        <span className="text-lg">{getFileIcon(file.file_type)}</span>
                        <span className="flex-1 text-gray-700">{file.filename}</span>
                        <span className="text-gray-500">{file.file_type.toUpperCase()}</span>
                      </div>
                    )) || (
                      <p className="text-sm text-gray-500">No files available</p>
                    )}
                  </div>
                </div>

                {/* Assigned Segments */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Assigned Segments</h4>
                  <div className="space-y-2">
                    {resource.assigned_segments?.map((segment) => (
                      <div key={segment.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{segment.name}</span>
                        {segment.is_primary && (
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs">
                            Primary
                          </span>
                        )}
                      </div>
                    )) || (
                      <p className="text-sm text-gray-500">No segments assigned</p>
                    )}
                  </div>
                </div>

                {/* Assigned Quiz Profiles */}
                {(resource.assigned_quiz_profiles?.length || 0) > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Assigned Quiz Profiles</h4>
                    <div className="space-y-2">
                      {resource.assigned_quiz_profiles?.map((profile) => (
                        <div key={profile.id} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">{profile.name}</span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                              {profile.slug}
                            </span>
                          </div>
                          {profile.is_primary && (
                            <span className="px-2 py-1 bg-purple-200 text-purple-900 rounded-full text-xs font-semibold">
                              Primary
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Resource Dates */}
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Created: {new Date(resource.created_at).toLocaleString()}</span>
                  {resource.updated_at && (
                    <span>Updated: {new Date(resource.updated_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ResourceList;